

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {IShortUser} from "@/model/shortInterfaces";
import {ILocker} from "@/model/Locker/lockerInterfaces";
import Datim from "@/components/shared/Datim.vue";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import UserChip from "@/components/shared/chips/UserChip.vue";
import {BoListPanel} from "bonomo/dist";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import {Prop, Watch} from "vue-property-decorator";
import LockerSearch from "@/components/locker/widget/LockerSearch.vue";
import LockerNav from "@/components/locker/widget/LockerNav.vue";
import PaginationBar from "@/components/shared/PaginationBar.vue";
import {downloadFileByUrl} from "@/helpers/downloadHelper";
import {EPermissionActions} from "@/model/EPermissionActions";

const lockerModule = namespace("lockerModule");

interface ILockerUserCsvRow extends IShortUser {
}

@Component({
  components: {
    BoListPanel,
    UserChip,
    LockerChip,
    Datim,
    LockerNav,
    LockerSearch,
    PaginationBar,
  }
})

export default class LockersUsers extends Vue {
  @Prop({default: 0})
  private lockerId!: number;

  @lockerModule.Getter('userList')
  private users!: IShortUser[];

  @lockerModule.Getter('lockers')
  private lockers!: ILocker[];

  @lockerModule.Getter('userListCurrentPage')
  private userListCurrentPage!: number;

  @lockerModule.Getter('userListLastPage')
  private userListLastPage!: number;

  @lockerModule.Getter('userListTotal')
  private userListTotal!: number;

  @lockerModule.Getter('userListPerPage')
  private userListPerPage!: number;

  @lockerModule.Action('clearUsers')
  protected clearUsers!: () => void;

  @lockerModule.Action('fetchLockerUsers')
  protected fetchLockerUsersAction!: ({lockerId, filters}: any) => Promise<any>;

  @lockerModule.Action('fetchLockerUsersRaw')
  protected fetchLockerUsersRawAction!: ({lockerId, filters}: any) => Promise<any>;

  protected selectedPerPage: number = 15;
  protected readonly perPageOptions: number[] = [15, 30, 50, 100];

  protected loading: boolean = false;
  protected downloadingCsv: boolean = false;

  protected get pageTitle(): string {
    return this.lockerId
        ? this.$t('lockers.users.title') + ' ' + this.getLockerName(this.lockerId)
        : '...';
  }

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('common.id'), align: 'start', sortable: false, value: 'id' },
      { text: this.$t('users.userList.email'), align: 'start', sortable: false, value: 'email' },
      { text: this.$t('users.userList.firstName'), align: 'start', sortable: false, value: 'first_name' },
      { text: this.$t('users.userList.lastName'), align: 'start', sortable: false, value: 'last_name' },
      { text: this.$t('users.userList.phone'), align: 'start', sortable: false, value: 'phone_number' },
      { text: this.$t('lockers.locker.title'), align: 'start', sortable: false, value: 'locker_id' },
      { text: this.$t('users.userList.createdAt'), align: 'start', sortable: false, value: 'created_at' },
      { text: '', value: 'detailButton' },
    ];
  }

  protected get canDownloadAllUsersCsv(): boolean {
    return this.$canAction(EPermissionActions.ACTION_LOCKERS_DOWNLOAD_USERS_BY_LOCKER);
  }

  mounted() {
    this.loadPage(1);
  }

  @Watch('lockerId')
  onLockerIdChanged() {
    this.clearUsers();
    this.loadPage(1);
  }

  private loadPage(page: number) {
    this.loading = true;
    this.fetchLockerUsersAction({ lockerId: this.lockerId, filters: { page, per_page: this.selectedPerPage } })
      .finally(() => { this.loading = false; });
  }

  protected onPageChange(page: number) {
    this.clearUsers();
    this.loadPage(page);
  }

  protected onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.clearUsers();
    this.loadPage(1);
  }

  toUserDetail(userId: number): any {
    return {name: 'user', params: { userId: `${userId}` }};
  }

  getLockerName(lockerId: number): string {
    for (let i = 0; i < this.lockers.length; i++) {
      if (this.lockers[i].id == lockerId) {
        return this.lockers[i].name;
      }
    }
    return '...';
  }

  private onLockerSelected(newLockerId: number) {
    this.$router.push({name: 'lockersUsers', params: {lockerId: '' + newLockerId}});
  }

  private async downloadUsersCsv() {
    if (!this.canDownloadAllUsersCsv) {
      return;
    }
    this.downloadingCsv = true;
    const users = await this.fetchLockerUsersRawAction({
      lockerId: this.lockerId,
      filters: {page: 0},
    }).catch(() => []);

    this.downloadingCsv = false;

    const headers = ['id', 'email', 'name', 'last_name', 'register_date', 'status'];
    const rows = this.normalizeUsersPayload(users).map((user: ILockerUserCsvRow) => [
      user.id,
      user.email || '',
      user.first_name || '',
      user.last_name || '',
      user.created_at || '',
      this.getStatus(user),
    ]);
    const csv = [headers, ...rows]
      .map((line: Array<string | number>) => line.map(this.escapeCsvCell).join(','))
      .join('\n');
    const csvBlob = new Blob(["\uFEFF" + csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(csvBlob);
    downloadFileByUrl(url, `locker-${this.lockerId}-users.csv`);
    URL.revokeObjectURL(url);
  }

  private normalizeUsersPayload(payload: any): ILockerUserCsvRow[] {
    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && Array.isArray(payload.data)) {
      return payload.data;
    }
    return [];
  }

  private getStatus(user: IShortUser): string {
    return user.active ? 'active' : 'inactive';
  }

  private escapeCsvCell(value: string | number): string {
    const safeValue = value === null || value === undefined ? '' : `${value}`;
    return `"${safeValue.replace(/"/g, '""')}"`;
  }
}
